import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSocket } from "../../context/SocketContext";
import { useTheme } from "../../context/ThemeContext";
import api from "../../api/riderApi";
import TripOffer from "../../components/TripOffer";
import { useRiderLocation } from "../../hooks/useRiderLocation";
import { playTripOfferChime } from "../../utils/audio";

export default function Home() {
  const { user, roles, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const socket = useSocket();
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(false);
  const [clockedIn, setClockedIn] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [tripOffer, setTripOffer] = useState(null);
  const [activeTrip, setActiveTrip] = useState(null);
  const [loading, setLoading] = useState(true);

  // Periodic GPS Ingestion while online (Plan 07)
  useRiderLocation({ enabled: isOnline });

  // Screen Wake Lock API management (Plan 04)
  const wakeLockRef = useRef(null);

  const requestWakeLock = useCallback(async () => {
    if (!("wakeLock" in navigator)) return;
    try {
      if (!wakeLockRef.current) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
        wakeLockRef.current.addEventListener("release", () => {
          wakeLockRef.current = null;
        });
      }
    } catch {}
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isOnline) {
      requestWakeLock();
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible" && isOnline) {
          requestWakeLock();
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
        releaseWakeLock();
      };
    } else {
      releaseWakeLock();
    }
  }, [isOnline, requestWakeLock, releaseWakeLock]);

  const fetchData = useCallback(async () => {
    try {
      const [profile, earningsRes, trip] = await Promise.all([
        api.get("/auth/me"),
        api.get("/earnings/today"),
        api.get("/trips/active"),
      ]);
      setIsOnline(
        profile.data.status === "available" ||
          profile.data.status === "on_delivery",
      );
      setClockedIn(
        !!earningsRes.data.shift && !earningsRes.data.shift.clock_out,
      );
      setEarnings(earningsRes.data);
      setActiveTrip(trip.data);
      if (trip.data) navigate("/rider/trip");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Listen for trip offers via Socket.io
  useEffect(() => {
    if (!socket) return;

    const handleOffer = (data) => {
      setTripOffer(data);
      // Vibrate and play real chime sound
      if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 200]);
      playTripOfferChime();
    };

    const handleTimeout = () => setTripOffer(null);
    const handleCancelled = () => {
      setTripOffer(null);
      setActiveTrip(null);
    };

    socket.on("trip:offer", handleOffer);
    socket.on("trip:timeout", handleTimeout);
    socket.on("trip:cancelled", handleCancelled);

    return () => {
      socket.off("trip:offer", handleOffer);
      socket.off("trip:timeout", handleTimeout);
      socket.off("trip:cancelled", handleCancelled);
    };
  }, [socket]);

  const toggleOnline = async () => {
    try {
      if (!clockedIn && !isOnline) {
        await api.post("/clock-in");
        setClockedIn(true);
      }
      const newStatus = isOnline ? "offline" : "available";
      await api.put("/status", { status: newStatus });
      setIsOnline(!isOnline);
      if (socket) socket.emit(isOnline ? "rider:offline" : "rider:online");
      if (isOnline) {
        // Clocking out
        await api.post("/clock-out");
        setClockedIn(false);
        fetchData();
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update status.");
    }
  };

  const acceptTrip = async (orderId) => {
    try {
      const res = await api.post(`/trips/${orderId}/accept`);
      setTripOffer(null);
      setActiveTrip(res.data);
      if (socket) socket.emit("trip:accept", { order_id: orderId });
      navigate("/rider/trip");
    } catch (err) {
      alert(err.response?.data?.error || "Failed to accept trip.");
    }
  };

  const declineTrip = async (orderId) => {
    try {
      await api.post(`/trips/${orderId}/decline`);
      if (socket) socket.emit("trip:decline", { order_id: orderId });
      setTripOffer(null);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Full-screen trip offer overlay
  if (tripOffer) {
    return (
      <TripOffer
        offer={tripOffer}
        onAccept={acceptTrip}
        onDecline={declineTrip}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950 transition-colors">
      {/* Header */}
      <div className="bg-blue-600 dark:bg-zinc-900 text-white px-5 pt-6 pb-8 rounded-b-3xl transition-colors">
        <div className="flex justify-between items-center mb-6">
          <div>
            <p className="text-blue-200 dark:text-gray-400 text-sm">
              Welcome back
            </p>
            <h1 className="text-xl font-bold">{user?.name}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="text-blue-200 dark:text-gray-400 hover:text-white p-1 rounded-lg transition-colors"
              title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
            >
              {theme === "dark" ? (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m0-12.728l.707.707m12.728 12.728l.707.707M12 8a4 4 0 100 8 4 4 0 000-8z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                </svg>
              )}
            </button>
            <button
              onClick={logout}
              className="text-blue-200 dark:text-gray-400 text-sm px-3 py-1 border border-blue-400 dark:border-zinc-700 rounded-lg hover:text-white dark:hover:text-white transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Online Toggle */}
        <button
          onClick={toggleOnline}
          className={`w-full py-4 rounded-2xl font-bold text-lg transition-all ${
            isOnline
              ? "bg-green-500 text-white shadow-lg shadow-green-500/30"
              : "bg-white/20 dark:bg-zinc-800 text-white border-2 border-white/40 dark:border-zinc-700"
          }`}
        >
          {isOnline ? "ONLINE" : "GO ONLINE"}
        </button>
        {!isOnline && (
          <p className="text-center text-blue-200 dark:text-gray-400 text-sm mt-2">
            Go online to receive delivery requests
          </p>
        )}
        {isOnline && (
          <p className="text-center text-green-100 dark:text-green-400 text-sm mt-2 animate-pulse">
            Waiting for delivery requests...
          </p>
        )}
      </div>

      {/* Today's Stats */}
      <div className="px-5 -mt-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-gray-100 dark:border-zinc-800 p-5 transition-colors">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">
            Today
          </h2>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {earnings?.deliveries_completed || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Deliveries
              </p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ${(earnings?.total_earnings || 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Earnings
              </p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ${(earnings?.commission || 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Commission
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Earnings Breakdown */}
      {clockedIn && earnings && (
        <div className="px-5 mt-4">
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-gray-100 dark:border-zinc-800 p-5 transition-colors">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">
              Earnings Breakdown
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Base pay
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  ${(earnings.base_pay || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Delivery commission ({earnings.deliveries_completed} x $0.50)
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  ${(earnings.commission || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  COD bonus
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  ${(earnings.cod_bonus || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between font-bold border-t dark:border-zinc-800 pt-2 mt-2">
                <span className="text-gray-900 dark:text-white">Total</span>
                <span className="text-blue-600 dark:text-blue-400">
                  ${(earnings.total_earnings || 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Links */}
      <div className="px-5 mt-4 pb-8 space-y-3">
        <Link
          to="/rider/earnings"
          className="block w-full bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-gray-100 dark:border-zinc-800 p-4 text-center text-blue-600 dark:text-blue-400 font-medium transition-colors"
        >
          View Earnings History
        </Link>
        <Link
          to="/rider/feedback"
          className="block w-full bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-gray-100 dark:border-zinc-800 p-4 text-center text-gray-600 dark:text-gray-400 font-medium transition-colors"
        >
          Give Feedback
        </Link>

        {!roles?.includes("seller") && (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-gray-100 dark:border-zinc-800 p-5 mt-6 text-center transition-colors">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">
              Platform Roles
            </h2>
            <button
              onClick={() =>
                alert("Coming soon: Fast-track User Registration!")
              }
              className="w-full bg-gray-50 dark:bg-zinc-850 text-gray-700 dark:text-gray-300 py-3 rounded-xl font-medium text-sm border border-gray-200 dark:border-zinc-700 hover:bg-gray-100 dark:hover:bg-zinc-800"
            >
              Also register as a User
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
